import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from "react";
import { api } from "../lib/api";
import { decodePlayerToken } from "../lib/token";
import { clearAuth, getAuthPlayer, getAuthToken, getCurrentOrg, setAuth } from "../lib/storage";
import type { AuthedPlayer } from "../lib/types";
import { clearWidgetSession, shareSessionWithWidget } from "../lib/nativeBridge";

export type Role = "worker" | "manager" | "admin";

type ClaimResult = { ok: true } | { ok: false; error: string; status: number };

type AuthState = {
  player: AuthedPlayer | null;
  orgId: number | null;
  // Effective role from GET /api/auth/me. Set when an invite is accepted from
  // the role the server returns with it; re-derived authoritatively from the
  // server on every fresh boot.
  role: Role | null;
  // True for a bare workspace-owner session (session role "owner", no
  // player chosen on this device) — see the bootstrap effect below. Nav
  // gating for /console (M5b) reads this.
  isOwner: boolean;
  loading: boolean;
  acceptInvite(inviteToken: string, pin?: string): Promise<ClaimResult>;
  logout(): void;
};

const AuthContext = createContext<AuthState | null>(null);

// Boots from whatever this browser last authenticated as (see storage.ts's
// org-keyed scheme), then re-validates against GET /api/auth/me. That call
// is now ALWAYS attempted, even with no locally-known org: a workspace owner
// signs in via the separate /login page (not part of this SPA) and carries
// an httpOnly trim_session cookie this component can't see locally — the
// only way to discover "I'm the owner" is to ask the server (player-routes.ts's
// /api/auth/me returns `{player: null, admin: true, role: "admin"}` for that
// case). For the player-token path this is unchanged from M5a: a 401 either
// way is treated as logged out, same as legacy static/js/auth.js's check().
export function AuthProvider({ children }: { children: ReactNode }) {
  const [orgId, setOrgId] = useState<number | null>(() => getCurrentOrg());
  const [player, setPlayer] = useState<AuthedPlayer | null>(() => (orgId !== null ? getAuthPlayer(orgId) : null));
  const [role, setRole] = useState<Role | null>(null);
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const currentOrg = getCurrentOrg();
      const res = await api.get("/api/auth/me").catch(() => null);
      if (cancelled) return;
      if (!res) {
        const cached = currentOrg === null ? null : getAuthPlayer(currentOrg);
        setPlayer(cached); setRole(cached ? "worker" : null); setIsOwner(false); setLoading(false); return;
      }
      if (res.status === 401 || res.status === 403) {
        if (currentOrg !== null) clearAuth(currentOrg);
        setPlayer(null);
        setRole(null);
        setIsOwner(false);
        setLoading(false);
        return;
      }
      const me: { player: { id: number; name: string } | null; admin?: boolean; role?: string } =
        await res.json().catch(() => ({ player: null }));
      if (me.player) {
        const authed = { id: me.player.id, name: me.player.name };
        if (currentOrg !== null) {
          setAuth(currentOrg, getAuthToken(currentOrg) || "", authed);
          setOrgId(currentOrg);
          const stored = getAuthToken(currentOrg);
          if (stored) shareSessionWithWidget(stored);
        }
        setPlayer(authed);
        setRole((me.role as Role) ?? null);
        setIsOwner(false);
      } else if (me.admin) {
        // Bare owner-console session — no player profile chosen on this
        // device. Leaves player/orgId untouched (there is none); nav gating
        // (BottomNav.tsx) reads role/isOwner directly.
        setPlayer(null);
        setRole("admin");
        setIsOwner(true);
      } else {
        if (currentOrg !== null) clearAuth(currentOrg);
        setPlayer(null);
        setRole(null);
        setIsOwner(false);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const applyToken = useCallback((token: string, authed: AuthedPlayer, knownRole?: Role) => {
    const decoded = decodePlayerToken(token);
    const org = decoded?.org ?? getCurrentOrg();
    if (org === null) throw new Error("could not determine organisation from token");
    setAuth(org, token, authed);
    shareSessionWithWidget(token);
    setOrgId(org);
    setPlayer(authed);
    setRole(knownRole ?? null);
    setIsOwner(false);
  }, []);

  const acceptInvite = useCallback(async (inviteToken: string, pin?: string): Promise<ClaimResult> => {
    const res = await api.postJson(`/api/invite/${encodeURIComponent(inviteToken)}/accept`, pin ? { pin } : {});
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: body.error || "This invite could not be used.", status: res.status };
    applyToken(body.token, body.player, body.role);
    return { ok: true };
  }, [applyToken]);

  const logout = useCallback(() => {
    if (orgId !== null) clearAuth(orgId);
    clearWidgetSession();
    setPlayer(null);
    setRole(null);
    setIsOwner(false);
  }, [orgId]);

  const value = useMemo(
    () => ({ player, orgId, role, isOwner, loading, acceptInvite, logout }),
    [player, orgId, role, isOwner, loading, acceptInvite, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
